/**
 * Network Quality Service: determina qualità connessione e strategia dati.
 * WiFi/4G → api_only; 3G → api_with_fallback; 2G/Offline → offline_only.
 */

import { Network, ConnectionStatus, ConnectionType } from '@capacitor/network';

export enum NetworkQuality {
  Excellent = 'excellent',
  Good = 'good',
  Tolerable = 'tolerable',
  Poor = 'poor',
  Offline = 'offline',
}

export enum DataStrategy {
  ApiOnly = 'api_only',
  ApiWithFallback = 'api_with_fallback',
  OfflineOnly = 'offline_only',
}

export interface NetworkState {
  connected: boolean;
  connectionType: ConnectionType;
  quality: NetworkQuality;
  strategy: DataStrategy;
  apiTimeout: number;
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number;
}

type NetworkStateListener = (state: NetworkState) => void;

const TIMEOUT_BY_QUALITY: Record<NetworkQuality, number> = {
  [NetworkQuality.Excellent]: 15000,
  [NetworkQuality.Good]: 10000,
  [NetworkQuality.Tolerable]: 5000,
  [NetworkQuality.Poor]: 0,
  [NetworkQuality.Offline]: 0,
};

const INITIAL_STATE: NetworkState = {
  connected: false,
  connectionType: 'none',
  quality: NetworkQuality.Offline,
  strategy: DataStrategy.OfflineOnly,
  apiTimeout: 0,
};

type NavigatorConnection = NetworkInformation & {
  addEventListener?: (type: string, cb: () => void) => void;
  removeEventListener?: (type: string, cb: () => void) => void;
};

function getNavigatorConnection(): NavigatorConnection | null {
  if (typeof navigator === 'undefined' || !('connection' in navigator)) return null;
  return (navigator as unknown as { connection?: NavigatorConnection }).connection ?? null;
}

interface NetworkInformation {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number;
}

class NetworkQualityServiceClass {
  private _state: NetworkState = { ...INITIAL_STATE };
  private _listeners = new Set<NetworkStateListener>();
  private _capacitorListener: { remove: () => Promise<void> } | null = null;
  private _navigatorConnectionHandler: (() => void) | null = null;
  private _windowOnlineHandler: (() => void) | null = null;
  private _windowOfflineHandler: (() => void) | null = null;

  subscribe(listener: NetworkStateListener): () => void {
    this._listeners.add(listener);
    listener(this._state);
    return () => this._listeners.delete(listener);
  }

  private _notify(): void {
    this._listeners.forEach((l) => l(this._state));
  }

  get currentState(): NetworkState {
    return this._state;
  }

  get canUseApi(): boolean {
    return this._state.strategy !== DataStrategy.OfflineOnly;
  }

  get isOffline(): boolean {
    return this._state.quality === NetworkQuality.Offline;
  }

  async initialize(): Promise<void> {
    try {
      this._capacitorListener = await Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        this._updateState(status);
      });

      const conn = getNavigatorConnection();
      if (conn?.addEventListener) {
        this._navigatorConnectionHandler = () => this._updateFromNavigator();
        conn.addEventListener('change', this._navigatorConnectionHandler);
      }

      const status = await Network.getStatus();
      this._updateState(status);
    } catch {
      this._updateFromNavigatorLine();
      this._registerWindowNetworkListeners();
    }
  }

  private _registerWindowNetworkListeners(): void {
    if (typeof window === 'undefined') return;
    this._windowOnlineHandler = () => void this.reevaluate();
    this._windowOfflineHandler = () => {
      this._state = {
        connected: false,
        connectionType: 'none',
        quality: NetworkQuality.Offline,
        strategy: DataStrategy.OfflineOnly,
        apiTimeout: 0,
      };
      this._notify();
    };
    window.addEventListener('online', this._windowOnlineHandler);
    window.addEventListener('offline', this._windowOfflineHandler);
  }

  destroy(): void {
    void this._capacitorListener?.remove();
    this._capacitorListener = null;
    const conn = getNavigatorConnection();
    if (conn?.removeEventListener && this._navigatorConnectionHandler) {
      conn.removeEventListener('change', this._navigatorConnectionHandler);
      this._navigatorConnectionHandler = null;
    }
    if (typeof window !== 'undefined') {
      if (this._windowOnlineHandler) {
        window.removeEventListener('online', this._windowOnlineHandler);
        this._windowOnlineHandler = null;
      }
      if (this._windowOfflineHandler) {
        window.removeEventListener('offline', this._windowOfflineHandler);
        this._windowOfflineHandler = null;
      }
    }
  }

  async reevaluate(): Promise<NetworkState> {
    try {
      const status = await Network.getStatus();
      this._updateState(status);
    } catch {
      this._updateFromNavigatorLine();
    }
    return this._state;
  }

  private _updateState(status: ConnectionStatus): void {
    if (!status.connected) {
      this._state = {
        connected: false,
        connectionType: 'none',
        quality: NetworkQuality.Offline,
        strategy: DataStrategy.OfflineOnly,
        apiTimeout: 0,
      };
      this._notify();
      return;
    }

    const quality = this._determineQuality(status.connectionType);
    const strategy = this._determineStrategy(quality);
    const navInfo = this._getNavigatorInfo();

    this._state = {
      connected: true,
      connectionType: status.connectionType,
      quality,
      strategy,
      apiTimeout: TIMEOUT_BY_QUALITY[quality],
      ...navInfo,
    };
    this._notify();
  }

  private _determineQuality(type: ConnectionType): NetworkQuality {
    const navInfo = this._getNavigatorInfo();
    if (navInfo.effectiveType) {
      switch (navInfo.effectiveType) {
        case '4g':
          return NetworkQuality.Good;
        case '3g':
          return NetworkQuality.Tolerable;
        case '2g':
        case 'slow-2g':
          return NetworkQuality.Poor;
      }
    }

    switch (type) {
      case 'wifi':
        return NetworkQuality.Excellent;
      case 'cellular':
        return NetworkQuality.Tolerable;
      case 'none':
      case 'unknown':
        return NetworkQuality.Offline;
      default:
        return NetworkQuality.Tolerable;
    }
  }

  private _determineStrategy(quality: NetworkQuality): DataStrategy {
    switch (quality) {
      case NetworkQuality.Excellent:
      case NetworkQuality.Good:
        return DataStrategy.ApiOnly;
      case NetworkQuality.Tolerable:
        return DataStrategy.ApiWithFallback;
      case NetworkQuality.Poor:
      case NetworkQuality.Offline:
        return DataStrategy.OfflineOnly;
    }
  }

  private _getNavigatorInfo(): Partial<NetworkState> {
    const conn = getNavigatorConnection();
    if (!conn) return {};
    return {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
    };
  }

  private _updateFromNavigator(): void {
    const current = this._state;
    if (!current.connected) return;

    const quality = this._determineQuality(current.connectionType);
    const strategy = this._determineStrategy(quality);

    this._state = {
      ...current,
      quality,
      strategy,
      apiTimeout: TIMEOUT_BY_QUALITY[quality],
      ...this._getNavigatorInfo(),
    };
    this._notify();
  }

  private _updateFromNavigatorLine(): void {
    const online = typeof navigator !== 'undefined' && navigator.onLine;
    if (!online) {
      this._state = {
        connected: false,
        connectionType: 'none',
        quality: NetworkQuality.Offline,
        strategy: DataStrategy.OfflineOnly,
        apiTimeout: 0,
      };
    } else {
      const quality = NetworkQuality.Tolerable;
      this._state = {
        connected: true,
        connectionType: 'wifi',
        quality,
        strategy: this._determineStrategy(quality),
        apiTimeout: TIMEOUT_BY_QUALITY[quality],
        ...this._getNavigatorInfo(),
      };
    }
    this._notify();
  }
}

export const networkQualityService = new NetworkQualityServiceClass();
